/**
 * US-180 / F-099 — what a scope actually hands over, shown before it is granted.
 *
 * Granting `residents` delivers the resident's clinical record — vitals with
 * 90-day trends, care notes, ADL records, a DNR flag, and a bulk PDF export of
 * the lot. The approver used to see the word "Residents".
 *
 * Shared by the request form and the approval dialog so the requester and the
 * approver read the SAME text; two copies of this would drift, which is the
 * problem US-159 and US-175 each fixed once. The copy itself lives in
 * @livingsyncs/types and is pinned to the routes it was written against.
 *
 * It describes what the grant PERMITS. It must not imply an approver can
 * withhold part of a scope — they cannot, and saying otherwise would be a
 * kinder lie than the bare label it replaces.
 */
import { List, Paper, Stack, Text } from "@mantine/core";
import {
  AUDIT_SCOPE_DISCLOSURE,
  AUDIT_SCOPE_LABELS,
  type AuditAccessScope,
} from "@livingsyncs/types";

interface AuditScopeContentsProps {
  /** Scopes to describe. Empty renders nothing. */
  scopes: readonly AuditAccessScope[];
  /** Heading above the list. */
  title?: string;
}

export function AuditScopeContents({
  scopes,
  title = "What these scopes include",
}: AuditScopeContentsProps) {
  if (scopes.length === 0) return null;

  return (
    <Paper withBorder p="sm" radius="sm" bg="var(--mantine-color-gray-0)">
      <Stack gap="xs">
        <Text size="sm" fw={600}>
          {title}
        </Text>
        {scopes.map((scope) => {
          const disclosure = AUDIT_SCOPE_DISCLOSURE[scope];
          if (!disclosure) return null;
          return (
            <Stack key={scope} gap={2}>
              <Text size="sm" fw={500}>
                {AUDIT_SCOPE_LABELS[scope]}
              </Text>
              <Text size="xs" c="dimmed">
                {disclosure.summary}
              </Text>
              <List size="xs" spacing={2} c="dimmed" withPadding>
                {disclosure.includes.map((item) => (
                  <List.Item key={item}>{item}</List.Item>
                ))}
              </List>
            </Stack>
          );
        })}
      </Stack>
    </Paper>
  );
}
